import { useState } from "react";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { solutionsContent } from "@/content";
import { interpolate } from "@/lib/rich";

interface FormState {
  name: string;
  phone: string;
  email: string;
  message: string;
}

const initialForm: FormState = {
  name: "",
  phone: "",
  email: "",
  message: "",
};

interface SolutionFormProps {
  solutionTitle: string;
  formId: string;
}

export default function SolutionForm({ solutionTitle, formId }: SolutionFormProps) {
  const copy = solutionsContent.form;
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const phoneMask = usePhoneMask();

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    phoneMask.onChange(event);
    setForm((prev) => ({ ...prev, phone: event.currentTarget.value }));
  };

  const handlePhoneFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    phoneMask.onFocus(event);
    setForm((prev) => ({ ...prev, phone: event.currentTarget.value }));
  };

  const handlePhoneBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    phoneMask.onBlur(event);
    setForm((prev) => ({ ...prev, phone: event.currentTarget.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMsg("");

    if (!form.name.trim()) {
      setErrorMsg(copy.errors.name);
      return;
    }
    if (!form.phone.trim()) {
      setErrorMsg(copy.errors.phone);
      return;
    }
    if (!form.email.trim()) {
      setErrorMsg(copy.errors.email);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErrorMsg(copy.errors.emailInvalid);
      return;
    }

    setStatus("submitting");

    const body = new URLSearchParams();
    body.append("name", form.name.trim());
    body.append("phone", form.phone.trim());
    body.append("email", form.email.trim());
    body.append("message", form.message.trim());
    body.append("solution", solutionTitle);
    body.append("page_path", window.location.pathname);
    body.append("form_name", `solutions-${solutionTitle}`);

    try {
      const response = await fetch(copy.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (response.ok) {
        setStatus("success");
        setForm(initialForm);
      } else {
        setStatus("error");
        setErrorMsg(copy.errors.generic);
      }
    } catch {
      setStatus("error");
      setErrorMsg(copy.errors.generic);
    }
  };

  if (status === "success") {
    return (
      <div className="bg-stone-900 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-600">
            <i className="ri-check-line text-white text-lg"></i>
          </div>
          <h4 className="text-white font-bold text-lg">{copy.successTitle}</h4>
        </div>
        <p className="text-white/60 text-sm">
          {interpolate(copy.successTextTemplate, { title: solutionTitle })}
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-4 text-sm text-orange-400 hover:text-orange-300 transition-colors cursor-pointer"
        >
          {copy.againLabel}
        </button>
      </div>
    );
  }

  return (
    <form
      id={formId}
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-stone-900 rounded-2xl p-6 md:p-8"
    >
      <h4 className="text-white font-bold text-lg mb-1">{copy.title}</h4>
      <p className="text-white/50 text-sm mb-5">
        {interpolate(copy.subtitleTemplate, { title: solutionTitle })}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-white/60 text-xs font-semibold mb-1.5">
            {copy.fieldLabels.name}
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={copy.namePlaceholder}
            className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-orange-600 transition-colors"
          />
        </div>
        <div>
          <label className="block text-white/60 text-xs font-semibold mb-1.5">
            {copy.fieldLabels.phone}
          </label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handlePhoneChange}
            onFocus={handlePhoneFocus}
            onBlur={handlePhoneBlur}
            onKeyDown={phoneMask.onKeyDown}
            placeholder={copy.phonePlaceholder}
            className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-orange-600 transition-colors"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-white/60 text-xs font-semibold mb-1.5">
          {copy.fieldLabels.email}
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder={copy.emailPlaceholder}
          className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-orange-600 transition-colors"
        />
      </div>

      <div className="mb-4">
        <label className="block text-white/60 text-xs font-semibold mb-1.5">
          {copy.fieldLabels.message}
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          maxLength={500}
          rows={3}
          placeholder={copy.messagePlaceholder}
          className="w-full bg-stone-800 border border-stone-700 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-orange-600 transition-colors resize-none"
        />
        <p className="text-white/50 text-xs mt-1 text-right">
          {form.message.length}/500
        </p>
      </div>

      <input type="hidden" name="solution" value={solutionTitle} />

      {errorMsg && <p className="text-red-400 text-sm mb-3">{errorMsg}</p>}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="text-sm font-bold px-6 py-3 rounded-full bg-orange-600 text-white hover:bg-orange-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? copy.submittingLabel : copy.submitLabel}
      </button>
    </form>
  );
}
